/* =========================================================================
   LAZ Skincare — routine client

   Three tiers of storage:
     1. localStorage "laz_routine" — the working copy. Everyone, guest or
        member, builds and edits here. No network for add/remove/reorder/cart.
     2. Customer metafield routine.saved (json) — a member's named routines,
        reached through the app proxy.
     3. Cloudflare KV — immutable share snapshots.

   BOUND ROUTINES AND AUTO-SYNC

   The working copy may be *bound* to a saved routine (source_id set), which
   happens when a member opens one or saves a new one. While bound, every
   mutation — including an add from a product page — is written through to the
   account automatically, debounced.

   That exists because the alternative was silent data loss: a member could
   open "Morning", add three products from product pages, open "Evening", and
   the three additions would vanish with no warning, never having reached the
   account. Writing through means there is nothing pending to lose.

   `unsynced` on the stored state is the honest record of whether the account
   has caught up. It survives navigation, so any page can warn before
   discarding work that never made it.

   Order is the catalog's, not the shopper's: items live under their step and
   the steps run in application order. Anything without a recognized step
   falls into "8 - Other".

   Exposes window.LazRoutine.
   ========================================================================= */

(function () {
  "use strict";

  const API = "/apps/regimen";
  const KEY = "laz_routine";
  const CHANGE_EVENT = "laz:routine:change";
  const SYNC_EVENT = "laz:routine:sync";
  const ROW_SECTION = "lsc-routine-row";
  const SCHEMA_VERSION = 1;

  const MAX_NAME = 60;
  const MAX_NOTE = 2000;

  /* Long enough to coalesce a burst of reorder clicks into one PUT, short
     enough that leaving the page straight after an add still lands. */
  const SYNC_DEBOUNCE_MS = 1200;

  /* Mirrors STEP_CHOICES in the Worker, the product metafield definition
     custom.regimen_step, and the nine step-N-* collections. If a merchant
     edits those choices, update the Worker first, then this, then add the
     matching collection — nothing syncs automatically. */
  const STEP_CHOICES = [
    "1 - Cleansers",
    "2 - Exfoliants",
    "3 - Toners & Mists",
    "4 - Masks",
    "5 - Serums",
    "5b - Eye Care",
    "6 - Moisturizers",
    "7 - Sunscreen",
    "8 - Other",
  ];
  const FALLBACK_STEP = "8 - Other";

  /* ---------------------------------------------------------------------
     working copy — localStorage
     --------------------------------------------------------------------- */

  function read() {
    let raw = null;
    try {
      raw = window.localStorage.getItem(KEY);
    } catch {
      return blank();
    }
    if (!raw) return blank();

    try {
      const parsed = JSON.parse(raw);
      const items = Array.isArray(parsed && parsed.items) ? parsed.items : [];
      return {
        version: SCHEMA_VERSION,
        source_id: typeof parsed.source_id === "string" ? parsed.source_id : null,
        name: cleanName(parsed.name),
        note: cleanNote(parsed.note),
        unsynced: parsed.unsynced === true,
        items: items.filter(isItem).map(normalizeItem),
      };
    } catch {
      return blank();
    }
  }

  function blank() {
    return {
      version: SCHEMA_VERSION,
      source_id: null,
      name: null,
      note: null,
      unsynced: false,
      items: [],
    };
  }

  function cleanName(value) {
    if (typeof value !== "string") return null;
    const name = value.trim().replace(/\s+/g, " ").slice(0, MAX_NAME);
    return name || null;
  }

  function cleanNote(value) {
    if (typeof value !== "string") return null;
    const note = value.trim().slice(0, MAX_NOTE);
    return note || null;
  }

  function isItem(item) {
    return item && typeof item === "object" && typeof item.variant_gid === "string";
  }

  function normalizeItem(item) {
    const step = item.step_label ? String(item.step_label) : "";
    return {
      variant_gid: String(item.variant_gid),
      product_gid: item.product_gid ? String(item.product_gid) : "",
      handle: item.handle ? String(item.handle) : "",
      step_label: STEP_CHOICES.includes(step) ? step : "",
      actives: Array.isArray(item.actives) ? item.actives.map(String) : [],
    };
  }

  /* Single write path, so every mutation notifies listeners the same way.
     options.sync marks this as a shopper edit the account should catch up
     with; loading a saved routine or clearing pass false. */
  function write(state, options = {}) {
    const shouldSync = options.sync === true && Boolean(state.source_id);

    const next = {
      version: SCHEMA_VERSION,
      source_id: state.source_id || null,
      name: cleanName(state.name),
      note: cleanNote(state.note),
      unsynced: shouldSync ? true : state.unsynced === true,
      items: (state.items || []).filter(isItem).map(normalizeItem),
    };

    try {
      window.localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      return false;
    }

    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { routine: next } })
    );

    if (shouldSync) scheduleSync();
    return true;
  }

  const items = () => read().items;
  const count = () => read().items.length;

  function indexOfVariant(variantGid) {
    return items().findIndex((i) => i.variant_gid === variantGid);
  }

  const hasVariant = (variantGid) => indexOfVariant(variantGid) !== -1;

  const hasProduct = (productGid) =>
    items().some((i) => i.product_gid && i.product_gid === productGid);

  /* Adding a second variant of a product the routine already holds replaces
     the first: a routine is a sequence of steps, not a shopping list. */
  function add(item) {
    if (!isItem(item)) return false;
    const state = read();
    const clean = normalizeItem(item);

    if (state.items.some((i) => i.variant_gid === clean.variant_gid)) return true;

    const existing = clean.product_gid
      ? state.items.findIndex((i) => i.product_gid === clean.product_gid)
      : -1;

    if (existing !== -1) {
      state.items[existing] = clean;
    } else {
      state.items.push(clean);
    }
    return write(state, { sync: true });
  }

  function remove(variantGid) {
    const state = read();
    state.items = state.items.filter((i) => i.variant_gid !== variantGid);
    return write(state, { sync: true });
  }

  function removeProduct(productGid) {
    const state = read();
    state.items = state.items.filter((i) => i.product_gid !== productGid);
    return write(state, { sync: true });
  }

  /* Moves an item by variant, not by index: the page lists items grouped
     under their step, so a row's position on screen is not its position in
     the array. Reordering only makes sense within a step. */
  function moveWithinStep(variantGid, direction) {
    const state = read();
    const from = state.items.findIndex((i) => i.variant_gid === variantGid);
    if (from === -1) return false;

    const step = state.items[from].step_label;

    let to = -1;
    if (direction < 0) {
      for (let i = from - 1; i >= 0; i--) {
        if (state.items[i].step_label === step) { to = i; break; }
      }
    } else {
      for (let i = from + 1; i < state.items.length; i++) {
        if (state.items[i].step_label === step) { to = i; break; }
      }
    }

    if (to === -1) return false;

    const [moved] = state.items.splice(from, 1);
    state.items.splice(to, 0, moved);
    return write(state, { sync: true });
  }

  /* Clearing never touches the saved routine it came from — it unbinds first,
     so the auto-sync has nothing to write through.

     LSC - async now, and it lands pending work before unbinding. See
     flushPending(). Callers that ignore the promise behave exactly as before. */
  async function clear() {
    await flushPending();
    return write(blank(), { sync: false });
  }

  /* LSC - Save anything outstanding before the state it belongs to is thrown
     away.

     replace() and clear() both write with { sync: false }, which neither
     cancelled the pending debounce timer nor flushed it. An edit made less
     than SYNC_DEBOUNCE_MS before opening a different saved routine was
     therefore lost twice over: the timer survived into the new state, and when
     it fired syncNow() read a routine with a different source_id and no
     pending flag, so it no-opped.

     Flushing here is the honest order of operations — finish with the old
     routine before picking up the next one. The timer is cleared either way,
     so nothing from the old state can fire against the new one. */
  async function flushPending() {
    const state = read();
    if (state.source_id && state.unsynced && state.items.length) {
      try {
        await syncNow();
      } catch {
        /* A failed flush must not block the shopper from opening another
           routine. The work stays in localStorage with unsynced still set. */
      }
    }
    window.clearTimeout(syncTimer);
  }

  function setMeta(meta) {
    const state = read();
    if (Object.prototype.hasOwnProperty.call(meta, "name")) state.name = meta.name;
    if (Object.prototype.hasOwnProperty.call(meta, "note")) state.note = meta.note;
    return write(state, { sync: true });
  }

  /* LSC - async, and it flushes first: see flushPending(). Opening a saved
     routine within the debounce window used to discard whatever was typed or
     dragged just before. Callers that ignore the promise are unaffected. */
  async function replace(nextItems, meta = {}) {
    await flushPending();
    return write(
      {
        source_id: meta.source_id || null,
        name: meta.name || null,
        note: meta.note || null,
        unsynced: false,
        items: nextItems || [],
      },
      { sync: false }
    );
  }

  /* LSC - One storage listener for the module, not one per subscriber.

     This used to add an anonymous `storage` listener inside onChange, so every
     caller added a listener that could never be removed, and a handler
     subscribed twice would run twice for a cross-tab write but once for a
     local one. Now a single listener re-dispatches the same CHANGE_EVENT that
     a local write dispatches, and onChange is just a document subscription —
     which means it has one code path and could be unsubscribed if we ever
     need that. */
  window.addEventListener("storage", (e) => {
    if (e.key !== KEY) return;
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { routine: read(), external: true } })
    );
  });

  function onChange(handler) {
    document.addEventListener(CHANGE_EVENT, handler);
  }

  /* ---------------------------------------------------------------------
     auto-sync for bound routines
     --------------------------------------------------------------------- */

  let syncTimer = null;
  let currentSyncState = "idle";
  let lastSyncError = null;

  const syncState = () => currentSyncState;
  const syncError = () => lastSyncError;

  function emitSync(state, error) {
    currentSyncState = state;
    lastSyncError = error || null;
    document.dispatchEvent(
      new CustomEvent(SYNC_EVENT, { detail: { state, error: lastSyncError } })
    );
  }

  function onSync(handler) {
    document.addEventListener(SYNC_EVENT, handler);
  }

  function scheduleSync() {
    emitSync("pending");
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncNow, SYNC_DEBOUNCE_MS);
  }

  /* LSC - What was actually sent, as a string, so the response can be matched
     against the state it describes.

     Only the three fields the account stores. A change to any of them while a
     PUT is in flight has to keep `unsynced` set; a change to anything else
     (source_id, which only replace() touches) is handled by replace() itself
     flushing first. */
  const syncedShape = (state) =>
    JSON.stringify({ name: state.name, note: state.note, items: state.items });

  /* LSC - A sync in flight, and whether another was asked for while it ran.

     scheduleSync() only ever cleared the TIMER, so two syncs could overlap:
     syncNow is exported, flushOnExit calls it, and visibilitychange and
     pagehide BOTH fire on a real navigation. Two PUTs with no ordering
     guarantee, last writer wins — and the loser might be the newer one. */
  let inFlight = null;
  let syncAgain = false;
  let retryTimer = null;
  let retryStep = 0;

  const RETRY_BACKOFF_MS = [3000, 10000, 30000];

  async function syncNow(options = {}) {
    window.clearTimeout(syncTimer);
    window.clearTimeout(retryTimer);

    /* Already saving: do not start a second PUT. Remember that something
       changed, and run once more when this one settles. */
    if (inFlight) {
      syncAgain = true;
      return inFlight;
    }

    const state = read();
    if (!state.source_id || !state.unsynced) {
      emitSync("idle");
      return true;
    }

    /* LSC - Deliberately left as an error that persists: the account will not
       store a routine with no products, and silently clearing `unsynced` here
       would throw away the fact that the shopper emptied it. The flag stays,
       the next real edit tries again, and nothing is lost. */
    if (!state.items.length) {
      emitSync("error", new Error("A routine needs at least one product."));
      return false;
    }

    emitSync("saving");

    const sent = syncedShape(state);

    inFlight = (async () => {
      try {
        await updateRoutine(
          state.source_id,
          { name: state.name, note: state.note, items: state.items },
          /* keepalive only for the exit flush — see flushOnExit. */
          options.keepalive ? { keepalive: true } : {}
        );
        markSynced(sent);
        retryStep = 0;
        emitSync("saved");
        return true;
      } catch (err) {
        /* Deleted from another device: unbind rather than retrying forever
           against an id that no longer exists. */
        if (err.status === 404) {
          const fresh = read();
          write({ ...fresh, source_id: null, unsynced: false }, { sync: false });
          retryStep = 0;
          emitSync("error", err);
          return false;
        }

        emitSync("error", err);
        scheduleRetry();
        return false;
      }
    })();

    try {
      return await inFlight;
    } finally {
      inFlight = null;
      /* Something was edited while that PUT was in the air. markSynced() will
         have left `unsynced` set, so this picks it up. */
      if (syncAgain) {
        syncAgain = false;
        scheduleSync();
      }
    }
  }

  /* LSC - A failed save used to sit in "error" until the shopper happened to
     edit again, which on a flaky connection means the routine quietly stops
     reaching the account for the rest of the session. Three backoff attempts,
     then wait for a real signal — a new edit, or the network coming back. */
  function scheduleRetry() {
    if (retryStep >= RETRY_BACKOFF_MS.length) return;
    const wait = RETRY_BACKOFF_MS[retryStep];
    retryStep += 1;
    window.clearTimeout(retryTimer);
    retryTimer = window.setTimeout(syncNow, wait);
  }

  window.addEventListener("online", () => {
    const state = read();
    if (state.source_id && state.unsynced) {
      retryStep = 0;
      syncNow();
    }
  });

  /* LSC - Only clear `unsynced` if the state still looks like what was sent.

     This is the fix for a silent data loss. markSynced() used to re-read and
     write `unsynced: false` unconditionally, so an edit made WHILE the PUT was
     in the air — the window is a whole network round trip — was marked as
     saved by a response that did not contain it. The scheduled follow-up sync
     then found nothing to do and the edit never reached the account.

     Comparing the three synced fields rather than a revision counter also
     covers an edit made in another tab, which a counter held in this tab's
     memory would miss. */
  function markSynced(sent) {
    const state = read();
    if (sent !== undefined && syncedShape(state) !== sent) {
      syncAgain = true;
      return;
    }
    write({ ...state, unsynced: false }, { sync: false });
  }

  /* LSC - keepalive, because this is the one call that races the page going
     away: without it the browser cancels the request as the document unloads
     and the edit is lost. The payload is a few KB against keepalive's 64KB
     limit. */
  function flushOnExit() {
    const state = read();
    if (state.source_id && state.unsynced) syncNow({ keepalive: true });
  }

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushOnExit();
  });
  window.addEventListener("pagehide", flushOnExit);

  /* ---------------------------------------------------------------------
     cart
     --------------------------------------------------------------------- */

  const variantId = (gid) => Number(String(gid).split("/").pop());

  async function addToCart(list) {
    const payload = (Array.isArray(list) ? list : [list])
      .map((entry) => (typeof entry === "string" ? entry : entry.variant_gid))
      .map(variantId)
      .filter((id) => Number.isFinite(id) && id > 0)
      .map((id) => ({ id, quantity: 1 }));

    if (!payload.length) throw new Error("Nothing to add to your cart.");

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: payload }),
    });

    let body = null;
    try {
      body = await res.json();
    } catch {
      /* fall through to the generic message below */
    }

    if (!res.ok) {
      const message =
        (body && (body.description || body.message)) ||
        "We couldn't add that to your cart.";
      const err = new Error(message);
      err.status = res.status;
      throw err;
    }

    await refreshCart();
    return body;
  }

  /* Two separate handshakes with the theme, and neither substitutes for the
     other. Both were previously wrong, so adding a routine to the cart left
     the header count stale AND never opened the drawer:

       1. <cart-count> in the header (theme.js) subscribes to the theme's
          pub/sub `cartUpdate` and reads `event.cart.item_count`, so the
          payload must carry a REAL cart object. /cart/add.js does not return
          one — it returns the added line items — hence the follow-up GET of
          /cart.js. The old code published to `window.publish` /
          `window.PUB_SUB_EVENTS`, which do not exist in this theme (only
          `theme.pubsub.*` does), so the publish silently never happened.

       2. <cart-drawer> (cart.js) listens on `document` for `cart:refresh`,
          re-fetches its own section, and then reads `event.detail.open`
          UNGUARDED. The old code dispatched with no `detail` at all, so that
          read threw on null and the drawer refreshed but never opened.

     If the shop is not using a drawer cart there is simply no listener and
     nothing happens, which is fine: callers show their own toast, and the
     header count still updates. We deliberately do NOT navigate to /cart —
     yanking someone out of a routine they are still building would be worse
     than a quiet add.

     `cart:build` has no listener in this theme, but it is left in place in
     case a third-party app is listening for it. */
  async function refreshCart(options = {}) {
    const pubsub = window.theme && window.theme.pubsub;

    if (pubsub && pubsub.publish && pubsub.PUB_SUB_EVENTS) {
      try {
        const cartUrl =
          (window.theme.routes && window.theme.routes.cart_url) || "/cart";
        const cart = await (
          await fetch(`${cartUrl}.js`, { headers: { Accept: "application/json" } })
        ).json();
        pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: "laz-routine",
          cart,
        });
      } catch {
        /* A stale header count must never stop the drawer from opening. */
      }
    }

    document.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: { open: options.open !== false },
      })
    );
    document.dispatchEvent(new CustomEvent("cart:build", { bubbles: true }));
  }

  /* ---------------------------------------------------------------------
     proxy api
     --------------------------------------------------------------------- */

  async function api(path, options = {}) {
    const res = await fetch(`${API}/${path}`, {
      ...options,
      headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    });

    let body;
    try {
      body = await res.json();
    } catch {
      throw new Error("Unexpected response from the server.");
    }

    if (!res.ok) {
      const err = new Error(body.error || "Request failed");
      err.status = res.status;
      err.code = body.code;
      err.body = body;
      throw err;
    }
    return body;
  }

  const get = (path) => api(path, { method: "GET" });
  const post = (path, body) => api(path, { method: "POST", body: JSON.stringify(body) });
  /* options carries keepalive for the exit flush; everything else passes
     straight through to fetch. */
  const put = (path, body, options = {}) =>
    api(path, { ...options, method: "PUT", body: JSON.stringify(body) });
  const del = (path) => api(path, { method: "DELETE" });

  function share(payload = {}) {
    const state = read();
    return post("share", {
      items: payload.items || state.items,
      name: payload.name !== undefined ? payload.name : state.name,
      note: payload.note !== undefined ? payload.note : state.note,
    });
  }

  const readShared = (token) => get(`shared?token=${encodeURIComponent(token)}`);

  const savedRoutines = () => get("routines");

  function createRoutine(payload = {}) {
    const state = read();
    return post("routines", {
      name: payload.name,
      note: payload.note !== undefined ? payload.note : state.note,
      items: payload.items || state.items,
    });
  }

  /* options is passed through to fetch — syncNow uses it for keepalive on the
     exit flush. */
  const updateRoutine = (id, patch, options) =>
    put(`routines/${encodeURIComponent(id)}`, patch, options);
  const deleteRoutine = (id) => del(`routines/${encodeURIComponent(id)}`);

  /* ---------------------------------------------------------------------
     product rows
     --------------------------------------------------------------------- */

  const rowCache = new Map();

  function fetchRow(handle, variantGid) {
    const id = variantGid ? variantId(variantGid) : null;
    const cacheKey = `${handle}::${id || ""}`;
    if (rowCache.has(cacheKey)) return rowCache.get(cacheKey);

    const rowUrl =
      `/products/${encodeURIComponent(handle)}?section_id=${ROW_SECTION}` +
      (id ? `&variant=${id}` : "");

    const promise = fetch(rowUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`Row fetch failed for ${handle}`);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const inner = doc.querySelector(".lsc-routine-row__inner");
        if (!inner) throw new Error(`No row markup for ${handle}`);
        return inner;
      });

    /* LSC - Caching the PROMISE dedupes concurrent callers, which is the point
       — but a rejected promise stayed in the map for the life of the page, so
       one flaky response meant that row rendered blank on every subsequent
       render with no way back. Dropping it on rejection means the next render
       simply tries again. */
    promise.catch(() => {
      if (rowCache.get(cacheKey) === promise) rowCache.delete(cacheKey);
    });

    rowCache.set(cacheKey, promise);
    return promise;
  }

  async function fetchRows(list) {
    const results = await Promise.allSettled(
      list.map((item) =>
        item.handle ? fetchRow(item.handle, item.variant_gid) : Promise.reject()
      )
    );

    return list.map((item, i) => ({
      item,
      node:
        results[i].status === "fulfilled" ? results[i].value.cloneNode(true) : null,
    }));
  }

  /* ---------------------------------------------------------------------
     steps and metafield helpers
     --------------------------------------------------------------------- */

  function stepRank(label) {
    const idx = STEP_CHOICES.indexOf(label);
    return idx >= 0 ? idx : STEP_CHOICES.indexOf(FALLBACK_STEP);
  }

  function groupByStep(entries, options = {}) {
    const includeEmpty = options.includeEmpty !== false;
    const groups = STEP_CHOICES.map((label) => ({ label, entries: [] }));
    const index = new Map(groups.map((g) => [g.label, g]));
    const fallback = index.get(FALLBACK_STEP);

    for (const entry of entries) {
      const bucket = index.get(entry.item.step_label) || fallback;
      bucket.entries.push(entry);
    }

    return includeEmpty ? groups : groups.filter((g) => g.entries.length);
  }

  function firstStep(node) {
    if (!node) return "";
    try {
      const steps = JSON.parse(node.dataset.steps || "[]");
      const valid = steps.filter((s) => STEP_CHOICES.includes(s));
      return valid.length ? valid[0] : "";
    } catch {
      return "";
    }
  }

  function activesOf(node) {
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.dataset.actives || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /* ---------------------------------------------------------------------
     dom helpers
     --------------------------------------------------------------------- */

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text; // textContent, never innerHTML
    return node;
  }

  function toast(message, tone = "info") {
    const existing = document.querySelector(".lsc-routine-toast");
    if (existing) existing.remove();

    const node = el("div", `lsc-routine-toast lsc-routine-toast--${tone}`, message);
    node.setAttribute("role", "status");
    document.body.appendChild(node);
    requestAnimationFrame(() => node.classList.add("is-visible"));

    setTimeout(() => {
      node.classList.remove("is-visible");
      setTimeout(() => node.remove(), 300);
    }, 4000);
  }

  /* ---------------------------------------------------------------------
     interaction warnings
     --------------------------------------------------------------------- */

  const ACTIVE_GROUPS = {
    "Glycolic Acid (AHA)": "aha",
    "Lactic Acid (AHA)": "aha",
    "Mandelic Acid (AHA)": "aha",
    "Salicylic Acid (BHA)": "bha",
    Retinoid: "retinoid",
    "Benzoyl Peroxide": "benzoyl_peroxide",
    "Vitamin C": "vitamin_c",
    "Azelaic Acid": "azelaic",
    "Kojic Acid": "kojic",
    Peptides: "peptides",
    Exosomes: "exosomes",
  };

  const SEVERITY_RANK = { caution: 0, sequence: 1, separate: 2 };

const INTERACTIONS = [
    { pair: ["retinoid", "aha"], severity: "sequence", headline: "Split between morning / evening or alternate", detail: "Both speed cell turnover. Layering them together may increase irritation and damage skin barrier." },
    { pair: ["retinoid", "bha"], severity: "sequence", headline: "Split between morning / evening or alternate", detail: "Both may increase skin sensitivity. Layering them together may increase irritation and damage skin barrier." },
    { pair: ["retinoid", "benzoyl_peroxide"], severity: "separate", headline: "Split between morning / evening or alternate", detail: "Both may increase skin sensitivity. Layering them together may increase irritation and damage skin barrier." },
    { pair: ["retinoid", "vitamin_c"], severity: "separate", headline: "Vitamin C in the morning, retinoid at night", detail: "In some products, layering them together may increase irritation and damage skin barrier." },
    { pair: ["retinoid", "azelaic"], severity: "caution", headline: "Introduce one at a time", detail: "Often recommended together, but add the second gradually and ease off if you see flaking." },
    { pair: ["retinoid", "kojic"], severity: "caution", headline: "Introduce one at a time", detail: "Both target pigmentation and both can irritate. Build up slowly." },
    { pair: ["retinoid", "exosomes"], severity: "sequence", headline: "Apply exosomes first, then retinoid", detail: "Wait 5-10 minutes after exosomes before applying retinoid so each layer absorbs properly." },
    { pair: ["aha", "bha"], severity: "caution", headline: "Consider alternating", detail: "Stacking separate acid products is where barrier damage tends to happen." },
    { pair: ["aha", "vitamin_c"], severity: "sequence", headline: "Use at different times", detail: "Both are low-pH and the combination commonly stings. Vitamin C morning, acids night." },
    { pair: ["bha", "vitamin_c"], severity: "caution", headline: "Space them apart", detail: "Better tolerated than AHA with vitamin C, but move them apart if you notice tightness." },
    { pair: ["aha", "benzoyl_peroxide"], severity: "sequence", headline: "Alternate rather than layering", detail: "Both may be drying; together they can irritate the skin and damage the barrier." },
    { pair: ["bha", "benzoyl_peroxide"], severity: "sequence", headline: "Alternate rather than layering", detail: "Both dry and irritate the skin, through different mechanisms. Layered together they can irritate the skin and damage the barrier." },
    { pair: ["aha", "azelaic"], severity: "caution", headline: "Introduce one at a time", detail: "Azelaic acid is gentle, but stacking onto an existing acid routine can be too much for sensitive skin." },
    { pair: ["bha", "azelaic"], severity: "caution", headline: "Introduce one at a time", detail: "Both are used for acne and can cause dryness. Adding them together at once increases the risk." },
    { pair: ["aha", "kojic"], severity: "caution", headline: "Watch for sensitivity", detail: "Kojic acid can sensitize on its own; AHA exfoliation increases that risk." },
    { pair: ["aha", "peptides"], severity: "caution", headline: "Apply peptides after acids absorb", detail: "Some peptides are sensitive to low pH. Spacing them a few minutes apart is safer." },
    { pair: ["aha", "exosomes"], severity: "sequence", headline: "Apply exosomes first, then acid", detail: "Wait 5-10 minutes after exosomes before applying AHA so each layer absorbs properly." },
    { pair: ["benzoyl_peroxide", "vitamin_c"], severity: "separate", headline: "Split between morning and evening", detail: "Benzoyl peroxide oxidizes vitamin C. Separating them preserves both." },
    { pair: ["benzoyl_peroxide", "azelaic"], severity: "caution", headline: "Watch for dryness", detail: "Often layered successfully, but safer if used separately." },
    { pair: ["vitamin_c", "azelaic"], severity: "caution", headline: "Introduce gradually", detail: "Effective together for pigmentation, but both at once may be too much for reactive skin." },
  ];


  const INTERACTION_MAP = new Map(
    INTERACTIONS.map((r) => [[...r.pair].sort().join("|"), r])
  );

  function groupsForActives(actives) {
    const groups = new Set();
    for (const name of actives || []) {
      const g = ACTIVE_GROUPS[name];
      if (g) groups.add(g);
    }
    return [...groups];
  }

  /* Compares every pair of products once, keeps the most serious rule that
     applies to each pair, then CONSOLIDATES by warning.

     Without the consolidation a routine repeats itself badly: two products
     that both carry retinoid + AHA + BHA alongside two peptide products fires
     "apply peptides after acids absorb" four separate times, each note
     identical except for the product names. Grouping by headline collapses
     that to one note listing all four pairings — the advice is stated once,
     and every product relationship behind it is still visible.

     Returns [{ severity, headline, details[], pairs[[a, b], …] }], most
     serious first. `details` is a list because two rules can share a headline
     while explaining different mechanisms (retinoid+AHA and retinoid+BHA are
     both "Alternate nights"); each distinct explanation is kept. */
  function scanInteractions(entries) {
    const byWarning = new Map();

    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        const a = groupsForActives(entries[i].actives);
        const b = groupsForActives(entries[j].actives);
        if (!a.length || !b.length) continue;

        let worst = null;
        for (const ga of a) {
          for (const gb of b) {
            if (ga === gb) continue;
            const rule = INTERACTION_MAP.get([ga, gb].sort().join("|"));
            if (!rule) continue;
            if (!worst || SEVERITY_RANK[rule.severity] > SEVERITY_RANK[worst.severity]) {
              worst = rule;
            }
          }
        }

        if (!worst) continue;

        const key = worst.headline;
        let group = byWarning.get(key);
        if (!group) {
          group = { severity: worst.severity, headline: worst.headline, details: [], pairs: [] };
          byWarning.set(key, group);
        }

        /* Keep the most serious severity seen for this headline, so a note
            never looks milder than the worst pairing under it. */
        if (SEVERITY_RANK[worst.severity] > SEVERITY_RANK[group.severity]) {
          group.severity = worst.severity;
        }

        if (worst.detail && !group.details.includes(worst.detail)) {
          group.details.push(worst.detail);
        }

        const pair = [entries[i].title, entries[j].title];
        const seen = group.pairs.some((p) => p[0] === pair[0] && p[1] === pair[1]);
        if (!seen) group.pairs.push(pair);
      }
    }

    return [...byWarning.values()].sort(
      (x, y) => SEVERITY_RANK[y.severity] - SEVERITY_RANK[x.severity]
    );
  }

  /* LSC - "Using these together" is a disclosure rather than a plain heading
     plus list.

     The open/shut state lives here, at module scope, rather than on the DOM,
     because renderInteractions tears the panel down and rebuilds it from
     scratch on every routine change (add a product, change a step, rename).
     State stored on the nodes would be destroyed by each of those rebuilds,
     so a reader who collapsed the notes would find them open again the moment
     they touched anything else on the page.

     LSC - Starts COLLAPSED. These notes are advice about a routine that is
     already assembled, not a step in assembling one, and expanded they push
     the routine itself below the fold on most screens. The heading carries a
     note count, so shut it still says how much there is to read — which is
     what makes collapsing-by-default honest rather than hiding something.
     Opening is remembered for the rest of the page's life. */
  let notesOpen = false;

  /* LSC - Disclosure chevron. Built as real SVG nodes rather than innerHTML,
     to match this file's rule of never assigning markup as a string. The
     glyph points down; the collapsed state rotates it in CSS. */
  function chevronIcon() {
    const NS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(NS, "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", "18");
    svg.setAttribute("height", "18");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
    const path = document.createElementNS(NS, "path");
    path.setAttribute("d", "M6 9l6 6 6-6");
    svg.appendChild(path);
    return svg;
  }

  function renderInteractions(findings, container) {
    container.textContent = "";
    if (!findings.length) {
      container.hidden = true;
      container.classList.remove("is-collapsed");
      return;
    }
    container.hidden = false;

    const list = el("ul", "lsc-routine-notes__list");
    list.setAttribute("role", "list");

    /* LSC - The accessible disclosure pattern: a heading whose entire text is
       a button. The <h2> stays on the outside because a <button> may only
       contain phrasing content — an <h2> nested inside a button is invalid and
       screen readers handle it inconsistently. This way the panel keeps its
       place in the document outline and the control is still a real button. */
    const heading = el("h2", "lsc-routine-notes__heading heading text-xl");
    const toggle = el("button", "lsc-routine-notes__toggle");
    toggle.type = "button"; // never submit: this panel can sit inside a form
    toggle.setAttribute("aria-controls", "LscRoutineNotesList");
    list.id = "LscRoutineNotesList";

    toggle.appendChild(document.createTextNode("Using these together"));

    /* The count keeps the collapsed state informative — shut, the heading
       alone would not say whether there is anything worth opening. */
    toggle.appendChild(
      el(
        "span",
        "lsc-routine-notes__count text-sm text-opacity",
        `(${findings.length} ${findings.length === 1 ? "note" : "notes"})`
      )
    );

    const chevron = el("span", "lsc-routine-notes__chevron");
    chevron.appendChild(chevronIcon());
    toggle.appendChild(chevron);

    heading.appendChild(toggle);
    container.appendChild(heading);

    /* One place that pushes state into the DOM, called on build and on every
       click, so the markup and `notesOpen` can never drift apart. */
    function applyNotesState() {
      toggle.setAttribute("aria-expanded", notesOpen ? "true" : "false");
      list.hidden = !notesOpen;
      container.classList.toggle("is-collapsed", !notesOpen);
    }

    toggle.addEventListener("click", () => {
      notesOpen = !notesOpen;
      applyNotesState();
    });

    for (const finding of findings) {
      const li = el("li", `lsc-routine-note lsc-routine-note--${finding.severity}`);
      li.appendChild(el("p", "lsc-routine-note__headline font-medium", finding.headline));

      /* Every pairing that triggered this one warning. */
      const pairs = el("ul", "lsc-routine-note__pairs");
      pairs.setAttribute("role", "list");
      for (const [a, b] of finding.pairs) {
        pairs.appendChild(
          el("li", "lsc-routine-note__pair text-xs text-opacity", `${a} + ${b}`)
        );
      }
      li.appendChild(pairs);

      for (const detail of finding.details) {
        li.appendChild(el("p", "lsc-routine-note__detail text-sm", detail));
      }

      list.appendChild(li);
    }

    container.appendChild(list);

    /* Applied last, once the list is in the document, so the initial state is
       set in one pass rather than flashing open and then closing. */
    applyNotesState();
  }

  /* ---------------------------------------------------------------------
     exports
     --------------------------------------------------------------------- */

  window.LazRoutine = {
    KEY,
    CHANGE_EVENT,
    SYNC_EVENT,
    STEP_CHOICES,
    FALLBACK_STEP,
    MAX_NAME,
    MAX_NOTE,

    read,
    items,
    count,
    add,
    remove,
    removeProduct,
    moveWithinStep,
    clear,
    setMeta,
    replace,
    hasVariant,
    hasProduct,
    indexOfVariant,
    onChange,
    cleanName,
    cleanNote,

    syncNow,
    syncState,
    syncError,
    onSync,

    stepRank,
    groupByStep,

    addToCart,
    refreshCart,
    variantId,

    api: { get, post, put, del },
    share,
    readShared,
    savedRoutines,
    createRoutine,
    updateRoutine,
    deleteRoutine,

    fetchRows,
    firstStep,
    activesOf,
    scanInteractions,
    renderInteractions,
    el,
    toast,
  };
})();
